// Path-addressed boolean-tree edit planners (Slice 3B.3). Each op resolves a
// NodePath into the `clause`-selected tree — `structure.whereTree` by default,
// `structure.havingTree` for `clause: 'having'` (Plan 3) — derives a byte
// range, and emits a PlannedByteEdit. Connector-aware removal/insertion
// mirrors the flat planner in applyStructuralEdit.where.ts, and so does the
// parenthesising of a multi-part predicate on add / modify and keeping written
// text out of a trailing line comment.

import type { BooleanNode, PlannedByteEdit, StructuralEdit } from '../types';
import type { BooleanClauseTarget } from '../types/structuralEdit';
import type { EditCtx } from './applyStructuralEdit';
import {
  deleteEdit,
  findKeyword,
  insertEdit,
  replaceEdit,
  scanStatementKeywords,
} from './applyStructuralEdit.shared';
import { clauseTreeRoot, resolveNodePath } from './whereNodePath';
import { outsideLineComments, parenthesizeCompound } from './wholePredicate';

type PathOp = Extract<
  StructuralEdit,
  { op: 'modify-where-node' | 'remove-where-node' | 'add-where-node' }
>;

export function planWherePathEdit(ctx: EditCtx, edit: PathOp): readonly PlannedByteEdit[] {
  switch (edit.op) {
    case 'modify-where-node':
      return planModifyWhereNode(ctx, edit);
    case 'remove-where-node':
      return planRemoveWhereNode(ctx, edit);
    case 'add-where-node':
      return planAddWhereNode(ctx, edit);
  }
}

function planModifyWhereNode(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-where-node' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const node = resolveNodePath(root, edit.path);
  if (!node) return [];
  const { startOffset, endOffset } = node.range;
  const text = outsideLineComments(ctx, startOffset, endOffset, parenthesizeCompound(edit.newText, ctx.options));
  return [replaceEdit(ctx, startOffset, endOffset, text, 'modify-where-node')];
}

function planRemoveWhereNode(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'remove-where-node' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root || edit.path.length === 0) return [];
  return removeAtPath(ctx, root, edit.path, edit.clause);
}

function removeAtPath(
  ctx: EditCtx,
  root: BooleanNode,
  path: readonly number[],
  clause: BooleanClauseTarget | undefined,
): readonly PlannedByteEdit[] {
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  const parent = resolveNodePath(root, parentPath);
  if (!parent || parent.kind !== 'group') return [];
  const children = parent.children;
  if (idx < 0 || idx >= children.length) return [];
  if (children.length === 1) {
    // Sole child — removing it removes the parent group too.
    if (parentPath.length === 0) {
      // Parent is the root: remove the entire clause (incl. its keyword).
      // Clause-aware anchor — same scanStatementKeywords + findKeyword
      // machinery planRemoveHaving uses for whole-clause removal.
      const kws = scanStatementKeywords(ctx);
      const clauseKw = findKeyword(kws, clause === 'having' ? 'HAVING' : 'WHERE');
      if (clauseKw) {
        return [deleteEdit(ctx, clauseKw.start, root.range.endOffset, 'remove-where-node:whole')];
      }
      return [deleteEdit(ctx, root.range.startOffset, root.range.endOffset, 'remove-where-node:whole-no-kw')];
    }
    return removeAtPath(ctx, root, parentPath, clause);
  }
  // Siblings present — drop the node with its adjacent connector (mirror flat remove).
  const start = idx === 0 ? children[0].range.startOffset : children[idx - 1].range.endOffset;
  const end = idx === 0 ? children[1].range.startOffset : children[idx].range.endOffset;
  return [deleteEdit(ctx, start, end, 'remove-where-node')];
}

function planAddWhereNode(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-where-node' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const group = resolveNodePath(root, edit.parentPath);
  if (!group || group.kind !== 'group' || group.children.length === 0) return [];
  const last = group.children[group.children.length - 1];
  const text = parenthesizeCompound(edit.text, ctx.options);
  const at = last.range.endOffset;
  return [insertEdit(ctx, at, outsideLineComments(ctx, at, at, ` ${edit.combinator} ${text}`), 'add-where-node')];
}
